import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import HeroSlider from "@/components/HeroSlider";
import { ArrowRight, Truck, CreditCard, Headphones, Shield, ChevronRight, Mic, Calculator, GitCompare } from "lucide-react";

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const CATEGORY_IMAGES = {
  "Materiales de Construcción": "https://images.pexels.com/photos/4395556/pexels-photo-4395556.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Herramientas": "https://images.pexels.com/photos/3877525/pexels-photo-3877525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Jardín": "https://images.pexels.com/photos/3999647/pexels-photo-3999647.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Electrodomésticos": "https://images.pexels.com/photos/5591460/pexels-photo-5591460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Pinturas": "https://images.pexels.com/photos/1669754/pexels-photo-1669754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Electricidad": "https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Sanitaria": "https://images.pexels.com/photos/6467856/pexels-photo-6467856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Climatización": "https://images.pexels.com/photos/5539540/pexels-photo-5539540.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function Home() {
  const [slides, setSlides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [slidesRes, categoriesRes, productsRes] = await Promise.all([
        api.get("/slides"),
        api.get("/categories"),
        api.get("/products/featured?limit=8")
      ]);
      setSlides(slidesRes.data);
      setCategories(categoriesRes.data);
      setFeaturedProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Truck, title: "Envío Gratis", desc: "En Maldonado" },
    { icon: CreditCard, title: "Todas las Tarjetas", desc: "Hasta 60 cuotas" },
    { icon: Headphones, title: "Atención 24/7", desc: "WhatsApp siempre" },
    { icon: Shield, title: "Garantía", desc: "En todos los productos" },
  ];

  const uniqueFeatures = [
    { 
      icon: WhatsAppIcon, 
      title: "WhatsApp 24/7", 
      desc: "Atención inmediata las 24 horas, los 7 días",
      color: "bg-[#25D366]",
      link: "https://wa.me/59899179773",
      external: true
    },
    { 
      icon: Mic, 
      title: "Búsqueda por Voz", 
      desc: "Decí lo que buscás y te lo encontramos",
      color: "bg-accent",
      link: null,
      action: "voice"
    },
    { 
      icon: Calculator, 
      title: "Calculadora", 
      desc: "Calculá cuánto material necesitás para tu obra",
      color: "bg-primary",
      link: "/calculadora"
    },
    { 
      icon: GitCompare, 
      title: "Comparador", 
      desc: "Compará hasta 4 productos lado a lado",
      color: "bg-primary",
      link: "/comparador"
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {/* Hero Slider */}
      <HeroSlider slides={slides} />

      {/* Unique Features - Destacado */}
      <section className="bg-gradient-to-b from-primary to-primary/95 py-8 md:py-10 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23fff" fill-opacity="1" fill-rule="evenodd"%3E%3Cpath d="M0 40L40 0H20L0 20M40 40V20L20 40"/%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {uniqueFeatures.map(({ icon: Icon, title, desc, color, link, external, action }) => {
              const Content = (
                <div className="group bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 md:p-5 hover:bg-white/20 transition-all duration-300 cursor-pointer h-full">
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className={`${color} w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-white text-base md:text-lg leading-tight">{title}</h3>
                      <p className="text-white/70 text-xs md:text-sm mt-1 leading-snug">{desc}</p>
                    </div>
                  </div>
                </div>
              );

              if (external) {
                return (
                  <a key={title} href={link} target="_blank" rel="noopener noreferrer">
                    {Content}
                  </a>
                );
              }
              
              if (link) {
                return (
                  <Link key={title} to={link}>
                    {Content}
                  </Link>
                );
              }

              return (
                <div key={title} onClick={() => {
                  if (action === "voice") {
                    // Trigger voice search
                    const voiceBtn = document.querySelector('[data-testid="voice-search-btn"]');
                    if (voiceBtn) voiceBtn.click();
                  }
                }}>
                  {Content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust badges - Compact */}
      <section className="bg-white border-b py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-8">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-center gap-2 text-muted-foreground">
                <Icon className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">{title}</span>
                <span className="hidden sm:inline text-xs">- {desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories - Refined design */}
      <section className="py-12 md:py-16 bg-muted/30" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground">CATEGORÍAS</h2>
              <p className="text-muted-foreground text-sm mt-1">Explorá nuestro catálogo completo</p>
            </div>
            <Link to="/tienda" className="text-primary hover:text-accent flex items-center gap-1 text-sm font-semibold transition-colors">
              Ver Todo <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {categories.slice(0, 8).map((category) => (
              <Link
                key={category.category_id}
                to={`/tienda?category=${category.category_id}`}
                className="group relative aspect-[4/3] rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                data-testid={`category-${category.slug}`}
              >
                <img
                  src={CATEGORY_IMAGES[category.name] || "https://images.pexels.com/photos/4395556/pexels-photo-4395556.jpeg"}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {/* Overlay oscuro sutil */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                {/* Borde inferior con color accent al hover */}
                <div className="absolute inset-x-0 bottom-0 h-1 bg-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                {/* Texto */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-semibold text-base md:text-lg drop-shadow-lg">
                    {category.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12 md:py-16" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground">PRODUCTOS DESTACADOS</h2>
              <p className="text-muted-foreground text-sm mt-1">Lo más buscado esta semana</p>
            </div>
            <Link to="/tienda?featured=true" className="text-primary hover:text-accent flex items-center gap-1 text-sm font-semibold transition-colors">
              Ver Todo <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {featuredProducts.map((product) => (
              <ProductCard key={product.product_id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner - Refined */}
      <section className="py-16 bg-gradient-to-r from-primary to-primary/90 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h2 className="font-heading text-3xl md:text-4xl mb-3">
                ¿NECESITÁS CALCULAR MATERIALES?
              </h2>
              <p className="text-white/80 max-w-lg">
                Usá nuestra calculadora para saber cuántos bloques, cemento, pintura y más necesitás para tu obra.
              </p>
            </div>
            <Link to="/calculadora">
              <Button className="bg-white text-primary hover:bg-accent hover:text-white font-bold uppercase tracking-wide h-14 px-10 text-base shadow-lg transition-all">
                Ir a la Calculadora
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
